/**
 * Hooks for the NDA: the text in force, its version history, and the acceptance record.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ndaApi } from '@api/endpoints/nda'
import { adminApi } from '@api/endpoints/admin'
import type { AdminUserDto } from '@api/types/admin'
import type { SaveNdaRequest } from '@api/types/nda'

const NDA_CURRENT_KEY = ['nda-current']
const NDA_VERSIONS_KEY = ['nda-versions']
const NDA_ACCEPTANCES_KEY = 'nda-acceptances'
const NDA_ROSTER_KEY = ['nda-user-roster']

/**
 * How many users to pull when working out who has *not* accepted. The list endpoint pages, and
 * there is no "count users" call, so this is a ceiling rather than a page — hitting it exactly
 * is reported as truncation rather than silently understating the outstanding list.
 */
const ROSTER_LIMIT = 1000

// ===================
// QUERY HOOKS
// ===================

/** The version in force. Short stale time: an editor who just published wants to see it. */
export function useNdaCurrent() {
  return useQuery({
    queryKey: NDA_CURRENT_KEY,
    queryFn: () => ndaApi.getCurrent(),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  })
}

/** Every version, newest first. */
export function useNdaVersions() {
  return useQuery({
    queryKey: NDA_VERSIONS_KEY,
    queryFn: () => ndaApi.getVersions(),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  })
}

/**
 * The acceptance record. Pass a version to narrow it, or undefined for every version.
 *
 * `enabled` exists so the page can hold off until it knows which version is in force — the
 * default view is that version, and firing an unfiltered read first would pull the entire
 * history to throw it away a moment later.
 */
export function useNdaAcceptances(version: number | undefined, enabled = true) {
  return useQuery({
    queryKey: [NDA_ACCEPTANCES_KEY, version ?? 'all'],
    queryFn: () => ndaApi.getAcceptances(version),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    enabled,
  })
}

/**
 * Everybody with an account, used only to subtract the acceptances and show who is still
 * stopped at the gate. Enabled on demand — the outstanding list is a tab, not the default view,
 * and this is a big read for a page that is mostly about the text.
 */
export function useUserRoster(enabled: boolean) {
  return useQuery({
    queryKey: NDA_ROSTER_KEY,
    queryFn: async () => {
      const response = await adminApi.getUsers({ page: 1, pageSize: ROSTER_LIMIT })
      const users: AdminUserDto[] = response.items
      return { users, truncated: users.length >= ROSTER_LIMIT, limit: ROSTER_LIMIT }
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled,
  })
}

// ===================
// MUTATION HOOKS
// ===================

/**
 * Saves the text.
 *
 * Invalidates everything on the way out rather than seeding the cache: a save that minted a
 * version changes the acceptance picture for every user in the system, and the response only
 * carries the document.
 */
export function useSaveNda() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: SaveNdaRequest) => ndaApi.save(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NDA_CURRENT_KEY })
      queryClient.invalidateQueries({ queryKey: NDA_VERSIONS_KEY })
      queryClient.invalidateQueries({ queryKey: [NDA_ACCEPTANCES_KEY] })
    },
  })
}
